import { useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { TimesheetRowSingleDay } from '../../../types';
import { AvatarCell } from '../../../components/Table/CellRenderers/AvatarCell';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { Typography } from '../../../components/Typography/Typography';
import { useAtom } from 'jotai';
import { overideDataAtom } from '../../../state/UITimesheetsState';
import { LightNumericEditableCell } from '../components/SingleTimesheetTable/LightNumericEditableCell';
import { LightTotalCell } from '../components/SingleTimesheetTable/LightTotalCell';
import { LightDateHeaderCell } from '../components/SingleTimesheetTable/LightTableHeaderCell';

const columnHelper = createColumnHelper<TimesheetRowSingleDay>();

export const useOverideHolidayTableColumn = (dataHeader: string, holidayTooltipLabel: string) => {
  const { colors } = useTheme();
  const [overideHolidayData, setOverideHolidayData] = useAtom(overideDataAtom);

  const total = useMemo(() => {
    if (!overideHolidayData) return 0;
    return overideHolidayData.data.reduce((acc, item) => acc + (item.data || 0), 0);
  }, [overideHolidayData]);

  const onChangeData = useCallback(
    (value: number, id: number) => {
      setOverideHolidayData((prev) => {
        if (!prev) return prev;
        const newData = {
          ...prev,
          data: prev.data.map((item) => {
            if (item.id === id) {
              return { ...item, data: value };
            }
            return item;
          }),
        };
        return newData;
      });
    },
    [setOverideHolidayData]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: (info) => {
          const isFixedDeal = info.row.original.dealId === 0;
          return (
            <AvatarCell
              name={info.row.original.name}
              avatarBoxStyle={{
                background: isFixedDeal
                  ? colors.surfaceBackground.highlighted
                  : colors.surfaceIndicator.needsReview,
              }}
              avatarLetterStyle={{
                color: colors.text.main,
              }}
            />
          );
        },
        enableSorting: false,

        header: () => <TableHeaderCell text='Deal' />,
        footer: () => {
          return (
            <Typography
              variant='body'
              color={colors.text.main}
              style={{
                paddingLeft: '30px',
                fontSize: '17px',
              }}
            >
              Total
            </Typography>
          );
        },
        meta: {
          tdStyles: { paddingLeft: '16px', borderRight: `none`, width: '300px', maxWidth: '300px' },
          thStyles: { borderRight: `none`, paddingLeft: '16px', width: '300px', maxWidth: '300px' },
        },
      }),
      columnHelper.accessor('data', {
        cell: (info) => {
          return (
            <LightNumericEditableCell
              value={info.getValue()}
              onChange={(value) => onChangeData(value, info.row.original.id)}
            />
          );
        },
        header: () => <LightDateHeaderCell text={dataHeader} holidayName={holidayTooltipLabel} />,
        footer: () => <LightTotalCell total={total} />,
        enableSorting: false,
        meta: {
          tdStyles: { paddingLeft: '16px', borderRight: `none` },
          thStyles: {
            borderRight: `none`,
          },
        },
      }),
    ],
    [
      colors.surfaceBackground.highlighted,
      colors.surfaceIndicator.needsReview,
      colors.text.main,
      dataHeader,
      holidayTooltipLabel,
      onChangeData,
      total,
    ]
  );

  return columns;
};
